// CollabbayDashboard.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigation } from "@react-navigation/native";

// ✅ Show follower range (always, even for subscribed brands)
const getFollowerRange = (num) => {
  const n = Number(num) || 0;
  if (n < 2000) return "Under 2K Followers";
  if (n < 5000) return "2K–5K Followers";
  if (n < 10000) return "5K–10K Followers";
  if (n < 20000) return "10K–20K Followers";
  if (n < 50000) return "20K–50K Followers";
  if (n < 100000) return "50K–100K Followers";
  if (n < 200000) return "100K–200K Followers";
  if (n < 500000) return "200K–500K Followers";
  if (n < 1000000) return "500K–1M Followers";
  return "1M+ Followers";
};

// ✅ Ensure we extract a pure number from strings like "12.3k" or "10,000"
const parseFollowers = (val) => {
  if (!val) return 0;
  let str = String(val).toLowerCase().trim();
  if (str.endsWith("k")) return parseFloat(str) * 1000;
  if (str.endsWith("m")) return parseFloat(str) * 1000000;
  return parseInt(str.replace(/[^0-9]/g, ""), 10) || 0;
};

export default function CollabbayDashboard(props) {
  let { navigation } = props || {};
  // Fallback if this component is ever rendered without a nav prop
  if (!navigation?.navigate) {
    navigation = useNavigation();
  }

  // ------------------------------
  // Global state
  // ------------------------------
  const [userData, setUserData] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [venueCode, setVenueCode] = useState("");
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const [brandListings, setBrandListings] = useState([]);

  const [listingViewsById, setListingViewsById] = useState({});


  const [totalCollabCount, setTotalCollabCount] = useState(0);

  const [collabs, setCollabs] = useState([]);
  const [loadingCollabs, setLoadingCollabs] = useState(true);

  const [recentOnline, setRecentOnline] = useState([]);
  const [recentCount, setRecentCount] = useState(0);

  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [loadingSuggested, setLoadingSuggested] = useState(false);

  const [ragItems, setRagItems] = useState([]);
  const [loadingRAG, setLoadingRAG] = useState(true);

  const IMAGE_BASE = "https://collabbayadmin.com/listings/";

  const [missedCollabs, setMissedCollabs] = useState([]);
  const [alertCollabs, setAlertCollabs] = useState([]);

  // Alert for “in-venue now”
  const [showCollabModal, setShowCollabModal] = useState(false);
  const [currentCollab, setCurrentCollab] = useState(null);

  // Venue code info modal
  const [showVenueCodeInfo, setShowVenueCodeInfo] = useState(false);

const [showSegmentModal, setShowSegmentModal] = useState(false);
const [segmentStep, setSegmentStep] = useState(1);
const [segmentQuality, setSegmentQuality] = useState("");
const [segmentRhythm, setSegmentRhythm] = useState("");
const [segmentSaving, setSegmentSaving] = useState(false);
const [segmentError, setSegmentError] = useState("");


const [showFreemiumPopup, setShowFreemiumPopup] = useState(false);
const [freemiumProofIndex, setFreemiumProofIndex] = useState(0);
const [freemiumCloseReady, setFreemiumCloseReady] = useState(false);

const freemiumProofs = [
  "Six By Nico — one visit, 200k+ views",
  "Le Grand Bus, Mayfair — 100+ booking requests",
  "Plied and Prejudice, West End — 1m views",
  'Del74 — "Can’t recommend it enough"',
];

  // Boot (full-page) loader
  const [boot, setBoot] = useState(true);
  const [bootMsg, setBootMsg] = useState("Loading your dashboard…");
  const funMsgs = [
    "Fetching your collabs…",
    "Matching you to creators…",
    "Counting your bookings…",
    "Polishing the tiles…",
    "Warming up the tiles…",
  ];
  useEffect(() => {
    if (!boot) return;
    let i = 0;
    setBootMsg(funMsgs[0]);
    const id = setInterval(() => {
      i = (i + 1) % funMsgs.length;
      setBootMsg(funMsgs[i]);
    }, 1200);
    return () => clearInterval(id);
  }, [boot]);

  const getCoverImage = (logoCsv) => {
    if (!logoCsv || !logoCsv.trim()) {
      return "https://collabbayadmin.com/NoListingImage.jpg";
    }
    const first = logoCsv
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)[0];
    if (!first) return "https://collabbayadmin.com/NoListingImage.jpg";
    const url = first.startsWith("http") ? first : IMAGE_BASE + first;
    // ✅ Avoid template literal to fix syntax error
    return url + "?t=" + Date.now();
  };

  const ts = (v, fb = Number.POSITIVE_INFINITY) => {
    const t = v ? Date.parse(v) : NaN;
    return Number.isNaN(t) ? fb : t;
  };
  const creditTS = (c) => ts(c?.CreditAvailableStart);
  const plainDateTS = (c) => ts(c?.Date);

  const displayCollabs = useMemo(() => {
    const visible = Array.isArray(collabs)
      ? collabs.filter((c) => c.BrandNotInterested !== "Y")
      : [];
    const byCreditAsc = [...visible].sort((a, b) => creditTS(a) - creditTS(b));
    if (isAuthenticated || byCreditAsc.length === 0) return byCreditAsc;

    // not auth: pick single free row (oldest by Date)
    const trialTop = [...visible].sort(
      (a, b) => plainDateTS(a) - plainDateTS(b)
    )[0];
    if (!trialTop) return byCreditAsc;
    const rest = byCreditAsc.filter((x) => x.CollabID !== trialTop.CollabID);
    return [trialTop, ...rest];
  }, [collabs, isAuthenticated]);

  const trialFreeCollabID = useMemo(() => {
    if (isAuthenticated) return null;
    if (!Array.isArray(collabs) || collabs.length === 0) return null;

    // same rule as displayCollabs: oldest by Date
    const visible = collabs.filter((c) => c.BrandNotInterested !== "Y");
    const trialTop = [...visible].sort(
      (a, b) => plainDateTS(a) - plainDateTS(b)
    )[0];
    return trialTop?.CollabID || null;
  }, [collabs, isAuthenticated]);

  const [searchName, setSearchName] = useState("");
  const [statusFilter, setStatusFilter] = useState("All"); // All | Upcoming | Expired | Redeemed | Pending | Cancelled | Invited
  const [sortMode, setSortMode] = useState("Soonest"); // Soonest | Latest | Created-Newest | Created-Oldest

  const filteredCollabs = useMemo(() => {
    let items = displayCollabs;

    if (searchName.trim()) {
      const q = searchName.trim().toLowerCase();
      items = items.filter((c) =>
        (c?.Promoter || "").toLowerCase().includes(q)
      );
    }

    if (statusFilter !== "All") {
      items = items.filter((c) => getStatus(c) === statusFilter);
    }

    const copy = [...items];
    if (sortMode === "Soonest") {
      copy.sort((a, b) => creditTS(a) - creditTS(b));
    } else if (sortMode === "Latest") {
      copy.sort((a, b) => creditTS(b) - creditTS(a));
    } else if (sortMode === "Created-Newest") {
      copy.sort((a, b) => plainDateTS(b) - plainDateTS(a));
    } else if (sortMode === "Created-Oldest") {
      copy.sort((a, b) => plainDateTS(a) - plainDateTS(b));
    }
    return copy;
  }, [displayCollabs, searchName, statusFilter, sortMode]);

  // ------------------------------
  // Utilities: inject global styles
  // ------------------------------
  const injectOnce = useRef(false);
  useEffect(() => {
    if (injectOnce.current) return;
    injectOnce.current = true;

    // Google font + CSS variables
    const font = document.createElement("link");
    font.rel = "preconnect";
    font.href = "https://fonts.gstatic.com";
    document.head.appendChild(font);

    const font2 = document.createElement("link");
    font2.href =
      "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap";
    font2.rel = "stylesheet";
    document.head.appendChild(font2);

    const style = document.createElement("style");
    style.innerHTML = `
      :root{
        --bg: #f3f3f3;
        --bg2:#ffffff;
        --card: rgba(255,255,255,0.88);
        --glass: rgba(255,255,255,0.94);
        --muted: #6b7280;
        --brand: #FDF05D;
        --text: #f9fafb;          /* used mainly in sidebar */
        --text-strong:#ffffff;
        --ok:#4CD964;
        --warn:#f2c94c;
        --bad:#eb5757;
        --accent:#111214;
      }

      html, body, #root, #app {
        min-height: 100%;
        background-color: var(--bg);
        margin:0;
        padding:0;
      }

      * { box-sizing: border-box; }

      body{
        font-family: Montserrat,system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;
        color-scheme: light;
      }

      video, img { background-color: transparent; }

      .wrap{
        display:flex;
        min-height:100vh;
        width:100%;
        max-width:none;
        margin:0;
        overflow-x:hidden;
        background:#f3f3f3;
      }

      .main{
        flex:1;
        display:flex;
        flex-direction:column;
        gap:20px;
        padding:24px 28px;
        background:#f3f3f3;
        margin-left:230px; /* offset for fixed sidebar */
        color:#111827;     /* main content text dark */
      }

      *{box-sizing:border-box}

      .sidebar{
        width:230px;
        height:100vh;
        position:fixed;
        top:0;
        left:0;
        padding:28px 22px 20px 22px;
        font-family:Montserrat,system-ui,-apple-system,Segoe UI, Roboto, Helvetica, Arial, sans-serif;
        background: radial-gradient(600px 420px at 0% 0%, rgba(253,240,93,0.18), transparent 60%), 
                    linear-gradient(180deg,#050509,#050505,#000000);
        border-right:1px solid rgba(255,255,255,0.08);
        backdrop-filter: blur(18px);
        display:flex;
        flex-direction:column;
        gap:14px;
        overflow-y:auto;
        z-index:50;
        color:var(--text);
      }
      .logoRow{display:flex;align-items:center;gap:10px;margin-bottom:12px}
      .logo{
        width:36px;height:36px;border-radius:18px;background:#000;display:inline-flex;align-items:center;justify-content:center;
      }
      .logo img{width:24px;height:24px;object-fit:contain;}
      .appName{
        font-family: Montserrat,system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;
        font-weight:800; letter-spacing:.2px; color:#f5f5f5;
      }
      .modeTag{
        text-transform: uppercase;
        letter-spacing: .18em;
        font-weight: 500;
        font-size: 10px;
        color: #b3b3b3;
        opacity: .95;
        margin-top: 2px;
      }
      .nav{
        margin-top:4px;
        display:flex;
        flex-direction:column;
        gap:6px;
      }
      .nav button{
        appearance:none;border:none;background:transparent;color:#f5f5f5;
        text-align:left;padding:9px 12px;border-radius:10px;display:flex;gap:10px;align-items:center;
        transition:.2s ease;cursor:pointer
      }
      .nav button:hover{background:rgba(255,255,255,0.06)}
      .nav .cta{
        background:var(--brand);color:#000;font-weight:700;justify-content:center;margin-top:8px;border-radius:999px;
      }
      .nav .cta:hover{filter:brightness(0.95)}
      .nav .pill{
        font-size:12px;background:#000;color:#fff;padding:2px 8px;border-radius:999px;margin-left:auto
      }

      .upgradeBtn{
        margin-top:10px;
        width:100%;
        border-radius:999px;
        padding:9px 12px;
        border:none;
        background:linear-gradient(135deg,#FDF05D,#ffe983);
        color:#000;
        font-weight:800;
        font-size:13px;
        display:flex;
        align-items:center;
        justify-content:flex-start;
        cursor:pointer;
      }
      .upgradeBtnTitleOnly{
        font-weight:800;
        font-size:13px;
      }

      .sideActions{
        margin-top:10px;
        display:flex;
        flex-direction:column;
        gap:8px;
      }

      .sideActionCard{
        width:100%;
        border-radius:14px;
        border:1px solid rgba(255,255,255,0.08);
        background:rgba(255,255,255,0.03);
        padding:8px 10px;
        display:flex;
        flex-direction:column;
        gap:4px;
        cursor:pointer;
        transition:background .15s ease, transform .15s ease, border-color .15s ease;
        color:#f5f5f5;
      }
      .sideActionCard:hover{
        background:rgba(255,255,255,0.06);
        border-color:rgba(255,255,255,0.14);
        transform:translateY(-1px);
      }
      .sideActionTitle{
        font-size:12px;
        font-weight:700;
        letter-spacing:.02em;
      }
      .sideActionSubtitle{
        font-size:11px;
        color:#b3b3b3;
      }

      .mapActionRow{
        display:flex;
        align-items:center;
        gap:8px;
      }

      .newBadge{
        display:inline-flex;
        align-items:center;
        justify-content:center;
        margin-left:6px;
        font-size:9px;
        padding:2px 6px;
        border-radius:999px;
        background:#FDF05D;
        color:#000;
        font-weight:800;
        text-transform:uppercase;
        letter-spacing:.06em;
      }

      .grid, .col, .list, .row, .nameRow, .offer, .offerBody { min-width: 0; }

      .nameRow { gap:10px; min-width:0; }
      .nameRow .promoter {
        flex:1;
        min-width:0;
        overflow:hidden;
        text-overflow:ellipsis;
        white-space:nowrap;
      }

      .row, .chip, .offerBody, .miniSub, .offerTitle {
        overflow-wrap:anywhere;
        word-break:break-word;
      }

      img, video { max-width:100%; height:auto; }

      .top{
        display:flex;align-items:center;justify-content:space-between;gap:16px;
      }
      .hello{
        display:flex;align-items:center;gap:12px;
        padding:12px 14px;border-radius:20px;

      }
      .hello img{width:32px;height:32px;border-radius:50%;object-fit:cover;background:#e5e7eb}
      .hello .hi{font-weight:700; font-size: 32px;color:#111827;}
      .badge{
        display:inline-flex;align-items:center;gap:6px;
        background:rgba(15,23,42,0.06);
        padding:6px 10px;border-radius:999px;font-size:12px;
        color:#111827;
      }
        
.hiAvatarBtn{
  width:48px;
  height:48px;
  border-radius:50%;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 auto;
  cursor:pointer;
  padding:0;
  border:none;
  background:transparent;
  overflow:hidden;
}

.hiAvatarBtn--empty{
  background:#000000;
  border:1px solid rgba(255,255,255,0.16);
}
.hiAvatarBtn--empty:hover{
  filter:brightness(1.08);
}

.hiAvatarBtn svg{ display:block; }

.hiAvatarBtn img{
  width:100%;
  height:100%;
  object-fit:cover;
  border-radius:50%;
  display:block;
}



      .recent{
        display:flex;align-items:center;gap:10px;
        background:rgba(255,255,255,0.9);
        border:1px solid rgba(15,23,42,0.06);
        font-size: 10px;
        padding:10px 12px;border-radius:20px;
        box-shadow:0 18px 40px rgba(15,23,42,0.06);
      }
      .avatars{display:flex;align-items:center}
      .av{width:31px;height:31px;border-radius:50%;border:2px solid #f3f3f3;margin-left:-8px;overflow:hidden;background:#e5e7eb}
      .av img{width:100%;height:100%;object-fit:cover}
      .greenDot{width:10px;height:10px;border-radius:50%;background:var(--ok);position:relative;left:-6px;top:-8px;border:2px solid #f3f3f3}
      .plus{font-size:12px;color:#6b7280;padding-left:6px}
      
      .grid{
        display:grid;
        grid-template-columns: 1.35fr 1fr;
        gap:18px;
        position:relative;
        margin-top:12px;
      }
      .col{display:flex;flex-direction:column;gap:18px}

      .card{
        background:var(--card);
        border:1px solid rgba(15,23,42,0.08);
        border-radius:18px;
        padding:16px;
        backdrop-filter: blur(16px);
        box-shadow:0 18px 45px rgba(15,23,42,0.10);
      }
      .cardHeader{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px}
      .cardTitle{font-weight:800;letter-spacing:.2px;color:#111827;}
      .linkBtn{
        display:inline-flex;align-items:center;gap:8px;border-radius:20px;
        padding:8px 12px;
        background:#fdf05d;
        cursor:pointer;
        font-weight:600;
        color:#111827;
        border:1px solid rgba(15,23,42,0.08);
      }
      .linkBtn:hover{background:rgba(15,23,42,0.07)}

      .upgradeStrip{
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:12px;
        border:1px solid rgba(253,240,93,0.4);
        background:linear-gradient(120deg,rgba(253,240,93,0.16),rgba(253,240,93,0.04));
      }
      .upgradeStripText{
        display:flex;
        flex-direction:column;
        gap:4px;
      }
      .upgradeStripTitle{
        font-weight:800;
        font-size:14px;
        color:#111827;
      }
      .upgradeStripSub{
        font-size:12px;
        color:#4b5563;
      }
      .upgradeStripBtn{
        border:none;
        border-radius:999px;
        padding:8px 12px;
        background:#FDF05D;
        color:#000;
        font-weight:800;
        font-size:13px;
        display:inline-flex;
        align-items:center;
        gap:6px;
        cursor:pointer;
        white-space:nowrap;
      }

      .pillBox{display:inline-flex;align-items:center;gap:10px;background:#0f1115;border:1px solid #21232a;border-radius:999px;padding:6px 10px}
      .countPill{font-size: 21px; background:#000;border-radius:999px;padding:2px 8px;font-weight:800;color:#FDF05D;}

      .hScroll{display:flex;overflow:auto;gap:12px;padding-bottom:6px}
      .miniCard{
        min-width:260px;height:72px;
        background:rgba(255,255,255,0.9);
        border:1px solid rgba(15,23,42,0.08);
        border-radius:14px;padding:10px;display:flex;gap:10px;align-items:center;
        box-shadow:0 14px 30px rgba(15,23,42,0.08);
      }
      .circle{width:46px;height:46px;border-radius:50%;overflow:hidden;background:#e5e7eb;flex:0 0 auto}
      .circle img{width:100%;height:100%;object-fit:cover}
      .miniTitle{font-weight:700;color:#111827;}
      .miniSub{font-size:14px;color:#6b7280}

      .venueCard{
        position:relative;
        border-radius:14px;overflow:hidden;
        background:#000;display:flex;align-items:flex-end;justify-content:center
      }
      .venueOverlay{position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,.2),rgba(0,0,0,.75))}
      .venueInner{position:relative;z-index:2;display:flex;flex-direction:column;align-items:center;gap:4px;padding-bottom:10px}
      .venueLabel{font-size:12px;color:#ddd}
      .venueValue{font-size:44px;font-weight:800;color:#fff;line-height:1}

      /* Collab list */
      .list{display:flex;flex-direction:column;gap:10px;margin-top:6px}
      .row{
        position:relative;
        background:rgba(255,255,255,0.96);
        border:1px solid rgba(15,23,42,0.08);
        border-radius:14px;
        padding:12px;
        display:flex;
        flex-direction:column;
        gap:8px;
        cursor:pointer;
        transition:.2s ease;
        box-shadow:0 12px 30px rgba(15,23,42,0.10);
      }
      .row:hover{transform:translateY(-1px)}
      .row.free{box-shadow:0 0 0 2px var(--ok) inset}
      .row.proposal{
  box-shadow:0 0 0 2px #2563eb inset;
  background:linear-gradient(180deg, rgba(239,246,255,0.98), rgba(255,255,255,0.98));
}

.proposalBadge{
  position:absolute;
  top:-8px;
  left:14px;
  background:#2563eb;
  color:#fff;
  font-weight:800;
  border-radius:999px;
  padding:4px 8px;
  font-size:12px;
  box-shadow:0 8px 18px rgba(37,99,235,0.22);
}

.proposalInfo{
  background:#eff6ff;
  border:1px solid #bfdbfe;
  color:#1e3a8a;
  border-radius:14px;
  padding:10px 12px;
  font-size:13px;
  font-weight:700;
  line-height:1.45;
  margin:8px 0 12px 0;
}
      .rowTop{display:flex;align-items:center;justify-content:space-between}
      .status{display:inline-flex;align-items:center;gap:6px;border-radius:999px;padding:4px 8px;font-size:12px;border:1px solid rgba(255,255,255,.1);color:#f9fafb;}
      .status.expired{background:#5f6368}
      .status.redeemed{background:#0b5d2c}
      .status.upcoming{background:#285a2b}
      .status.pending{background:#695c14}
      .status.cancelled{background:#5a1b1b}
      .status.invited{background:#5a5314}
      .nameRow{display:flex;align-items:center;gap:10px}
      .avatar{width:40px;height:40px;border-radius:50%;overflow:hidden;background:#e5e7eb;flex:0 0 auto}
      .avatar img{width:100%;height:100%;object-fit:cover}
      .promoter{font-weight:700;color:#111827;}

      .blurredName{
        filter: blur(4px);
        opacity:.85;
      }
      .blurredAvatar img{
        filter: blur(4px);
        opacity:.9;
      }

      /* Chevron on right of each row */
      .rowChevron{
        position:absolute;
        right:12px;
        top:50%;
        transform:translateY(-50%);
        display:flex;
        align-items:center;
        justify-content:center;
        pointer-events:none;
        opacity:0.7;
      }
      .row:hover .rowChevron{
        opacity:1;
        transform:translate(2px,-50%);
      }

      /* group chips on one line and allow wrapping */
      .chipRow{ display:flex; flex-wrap:wrap; gap:8px }

      .chip{
        display:inline-flex; align-items:center;
        padding:6px 10px; border-radius:12px;
        background:#f3f4f6; border:1px solid #e5e7eb;
        font-size:13px; font-weight:600;
        align-self:flex-start;
        width:fit-content; max-width:100%;
        white-space:normal;
        color:#111827;
      }

      .offerChip{
        background:rgba(253,240,93,0.16);
        border-color:rgba(253,240,93,.55);
      }
      .clientChip{
        background:rgba(249,250,251,1);
        border-color:#d1d5db;
      }

      .chip .label{ font-weight:800; margin-right:6px; opacity:.9 }

      /* Offers */
      .offer{
        background:rgba(255,255,255,0.96);
        border:1px solid rgba(15,23,42,0.08);
        border-radius:14px;
        overflow:hidden;
        box-shadow:0 16px 35px rgba(15,23,42,0.10);
      }
      .offerImg{height:110px;background:#111;position:relative}
      .viewsBadge{
  position:absolute;
  top:10px;
  left:10px;
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:6px 10px;
  border-radius:999px;
  background:rgba(0,0,0,0.55);
  border:1px solid rgba(255,255,255,0.18);
  color:#fff;
  font-weight: 700;
  font-size:14px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}
.viewsBadge svg{display:block}

      .offerImg img{width:100%;height:100%;object-fit:cover}
      .editFab{position:absolute;top:8px;right:8px;background:rgba(0,0,0,0.5);border:1px solid rgba(255,255,255,0.16);border-radius:999px;padding:6px}
      .offerBody{padding:12px}
      .offerTitle{font-weight:700;color:#111827;}
      .offerSub{font-size:12px;color:#4b5563}
      .live{display:inline-flex;align-items:center;gap:6px;border-radius:999px;padding:4px 8px;border:1px solid #2e6130;background:#17361a;color:#9cf0a2;font-weight:700;font-size:12px}
      .paused{display:inline-flex;align-items:center;gap:6px;border-radius:999px;padding:4px 8px;border:1px solid #555;background:#222;color:#eee;font-weight:700;font-size:12px}

      /* RAG */
      .rag{display:flex;flex-direction:column;gap:10px}
      .ragItem{border-radius:12px;border:2px solid transparent;padding:12px;background:#f9fafb}
      .ragItem.yellow{border-color:var(--warn)}
      .ragItem.red{border-color:var(--bad)}
      .ragTitle{font-weight:800;color:#111827;}
      .ragSub{color:#4b5563}

      /* Modals */
      .modalOverlay{position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;z-index:60}
      .modal{width:min(560px,92vw);background:#ffffff;border:1px solid rgba(15,23,42,0.12);border-radius:16px;padding:18px;box-shadow:0 25px 80px rgba(15,23,42,0.35);}
      .modalHeader{display:flex;align-items:center;justify-content:space-between;margin-bottom:6px}
      .x{cursor:pointer;border:none;background:transparent;color:#111827;font-size:20px}
      .ctaBtn{background:#fdf05d;border:none;border-radius:12px;padding:10px 14px;font-weight:800;cursor:pointer;color:#000;}
      .ghostBtn{background:transparent;border:1px solid rgba(15,23,42,.16);border-radius:12px;padding:10px 14px;color:#111827;cursor:pointer}
      .danger{background:#2a0f12;border:1px solid #6d1f26;color:#fef2f2;}
      .center{display:flex;align-items:center;justify-content:center}
      .muted{color:var(--muted)}
      .brand{color:var(--brand)}
      .hr{height:1px;background:rgba(15,23,42,.08);margin:10px 0}
      .grid2{display:grid;grid-template-columns:1fr 1fr;gap:12px}

      @media (max-width: 1050px){
        .wrap{max-width:100%}
        .grid{grid-template-columns:1fr}
      }

      .grid::before{
        content:"";
        position:absolute;
        inset:0;
        background:
          linear-gradient(180deg, rgba(243,244,246,0.96) 0px, rgba(243,244,246,0.92) 120px, rgba(243,244,246,0) 280px),
          radial-gradient(650px 420px at 15% 8%, rgba(253,240,93,0.23), transparent 60%),
          radial-gradient(520px 360px at 82% 22%, rgba(253,240,93,0.20), transparent 65%),
          radial-gradient(740px 480px at 58% 78%, rgba(253,240,93,0.16), transparent 70%);
        background-size: cover;
        background-repeat: no-repeat;
        z-index: 0;
        border-radius: 16px;
      }

      .grid > *{
        position: relative;
        z-index: 1;
      }

      /* --- Date chips --- */
      .dateWrap{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
      .dateTxt{color:#111827;font-weight:600}
      .whenPill{
        background:#FDF05D;color:#000;border-radius:10px;
        padding:4px 8px;font-weight:800;
        border:1px solid rgba(0,0,0,.18);
        display:inline-flex;align-items:baseline;gap:6px
      }
      .whenBig{font-size:14px;line-height:1}
      @media (min-width:880px){.whenBig{font-size:16px}}

      /* --- Recap banner --- */
      .recap{ display:grid; grid-template-columns: max-content 1fr; gap:12px; align-items:stretch; }
      .recapTile{ min-height:140px; border-radius:16px; overflow:hidden; background:#000; border:1px solid rgba(15,23,42,0.10); backdrop-filter: blur(12px) }

      .recapVideo{ display:block; }
      .recapVideo video{
        display:block;
        height:150px;
        width:auto;
        object-fit:cover;
      }

      .recapCard{
        display:flex; align-items:center; justify-content:space-between; gap:16px;
        padding:14px 16px;
        background: linear-gradient(180deg, rgba(255,255,255,.98), rgba(249,250,251,.98));
        min-height:140px;
      }
      .recapText{ display:flex; flex-direction:column; gap:6px; padding-left: 7px; }
      .recapLabel{ font-size:12px; color:#6b7280; font-weight:700; letter-spacing:.2px }
      .recapValue{ font-size:36px; line-height:1; font-weight:800; color:#111827 }

      .recapCTA{
        display:inline-flex; align-items:center; gap:10px;
        padding:10px 14px; border-radius:999px; border:1px solid rgba(15,23,42,.16);
        background: rgba(255,255,255,0.96); color:#111827; cursor:pointer; font-weight:620;
        transition: filter .18s ease, transform .18s ease;
        white-space:nowrap;
      }
      .recapCTA:hover{ filter:brightness(1.02); transform:translateY(-1px) }

      @media (max-width:900px){
        .recap{ grid-template-columns: 1fr; }
        .recapTile{ height:130px; }
        .recapValue{ font-size:30px; }
      }

      /* Sidebar creators section */
      .sideCreators{
        margin-top:12px;
        padding-top:6px;
        border-top:1px solid rgba(255,255,255,0.08);
      }
      .sideCreatorsHeader{
        display:flex; align-items:center; justify-content:space-between;
        margin:8px 0 8px 0;
        color:#f5f5f5;
      }
      .sideCreatorsTitle{ font-weight:800; font-size:13px; letter-spacing:.2px; }
      .sideCreatorsHint{ font-size:11px; color:#b3b3b3; }
      .sideList{
        display:flex; flex-direction:column; gap:8px;
        max-height:220px; overflow:auto; padding-right:2px;
      }
      .sideCreatorBtn{
        display:flex; align-items:center; gap:10px; width:100%;
        background:rgba(255,255,255,0.06);
        border:1px solid rgba(255,255,255,0.10);
        border-radius:12px; padding:8px 10px; text-align:left;
        cursor:pointer; transition:transform .15s ease, background .15s ease;
        color:#f5f5f5;
      }
      .sideCreatorBtn:hover{ transform:translateY(-1px); background:rgba(255,255,255,0.10); }
      .sideAvatar{
        width:30px; height:30px; border-radius:50%; overflow:hidden; background:#333; flex:0 0 auto;
      }
      .sideAvatar img{ width:100%; height:100%; object-fit:cover; }
      .sideMeta{ display:flex; flex-direction:column; min-width:0; }
      .sideName{ font-weight:700; font-size:13px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
      .sideSub{ font-size:11px; color:#d1d5db; }

      .sideHelpBtn{
        margin-top:auto;
        width:100%;
        border-radius:14px;
        border:1px solid rgba(255,255,255,0.16);
        background:rgba(255,255,255,0.03);
        padding:9px 11px;
        font-size:11px;
        display:flex;
        flex-direction:column;
        gap:2px;
        color:#f5f5f5;
        cursor:pointer;
        text-align:left;
      }
      .sideHelpMain{
        font-weight:700;
        font-size:12px;
      }
      .sideHelpSub{
        font-size:11px;
        opacity:.85;
      }

      /* Calendar controls */
      .controlsBar{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:8px}
      .searchBox{flex:1;min-width:180px;display:flex;align-items:center;gap:8px;background:#ffffff;border:1px solid #e5e7eb;border-radius:12px;padding:8px 10px}
      .searchBox input{all:unset;flex:1;font-size:14px;color:#111827;}
      .clearBtn{background:transparent;border:none;color:#6b7280;cursor:pointer}
      .sortSelect{border:1px solid #e5e7eb;background:#ffffff;color:#111827;border-radius:10px;padding:8px 10px;font-weight:700}

      /* Boot overlay */
      .bootOverlay{
        position:fixed; inset:0; z-index:9999;
        display:flex; align-items:center; justify-content:center;
        background:rgba(0,0,0,.75); backdrop-filter:blur(6px);
      }
      .spinner{
        width:42px; height:42px; border-radius:50%;
        border:3px solid rgba(255,255,255,.18); border-top-color:#FDF05D;
        animation:spin .9s linear infinite;
      }
      @keyframes spin { to { transform:rotate(360deg) } }
      .bootMsg{ font-weight:800; font-size:16px; margin-top:12px; color:#f9fafb; }

      /* Info button next to venue code */
      .infoBtn{
        width:18px;height:18px;border-radius:50%;
        border:1px solid rgba(156,163,175,0.9);
        background:transparent;
        color:#111827;
        font-size:11px;
        display:flex;align-items:center;justify-content:center;
        cursor:pointer;
      }

      .freemiumModal{
  width:min(460px,92vw);
  background:#fff;
  border-radius:24px;
  padding:22px;
  position:relative;
  text-align:center;
  border:1px solid rgba(17,24,39,0.08);
  box-shadow:0 30px 90px rgba(0,0,0,0.35);
}

.freemiumClose{
  position:absolute;
  top:12px;
  right:12px;
  width:30px;
  height:30px;
  border:none;
  border-radius:999px;
  background:#f3f3f3;
  font-size:18px;
  font-weight:900;
  cursor:pointer;
  color:#111827;
}

.freemiumClose.locked{
  opacity:.35;
  cursor:not-allowed;
}

.freemiumTitle{
  font-size:24px;
  line-height:1.15;
  font-weight:700;
  margin-bottom: 15px;
}
.freemiumTryBtn{
  width:100%;
  border:none;
  border-radius:999px;
  padding:14px;
  background:#fdf05d;
  color:#000;
  font-size:15px;
  font-weight:900;
  cursor:pointer;
}

.freemiumProof{
  margin-top:14px;
  background:#f3f7ff;
  border:1px solid #dbeafe;
  border-radius:14px;
  padding:11px 12px;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  font-size:13px;
  font-weight:800;
  color:#111827;
}

.verifyTick{
  width:18px;
  height:18px;
  border-radius:999px;
  background:#1d9bf0;
  color:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:11px;
  font-weight:900;
  flex:0 0 auto;
}
      .infoBtn:hover{
        background:rgba(243,244,246,0.9);
      }

      @media (max-width: 800px){
        .main{
          margin-left:230px;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);

  // ------------------------------
  // Fetch: session + auth
  // ------------------------------
  useEffect(() => {
    fetch("https://collabbayadmin.com/APIControllers/GetSessionData.php", {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => {
        setUserData(d || {});
        if (!d?.Username) setShowLoginPrompt(true);
        if (d?.VenueCode) setVenueCode(d.VenueCode || "");
      })
      .catch(() => {
        setUserData({});
        setShowLoginPrompt(true);
      });
  }, []);



useEffect(() => {
  if (!userData?.Username) return;

  fetch("https://collabbayadmin.com/APIControllers/CheckIfSegmented.php", {
    credentials: "include",
  })
    .then((r) => r.json())
    .then((d) => {
      if (d?.success && d?.requiresSegmentation) {
        setShowSegmentModal(true);
      }
    })
    .catch(() => {});
}, [userData?.Username]);


  useEffect(() => {
    fetch("https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => {
        const ok = d?.authenticated === true;
        setIsAuthenticated(ok);
        setErrorMessage(ok ? "" : "Subscribe to access collabbay features");
      })
      .catch(() =>
        setErrorMessage("An error occurred while checking authentication.")
      );
  }, []);

  useEffect(() => {
  if (!userData?.Username) return;
  if (isAuthenticated) return;

  const sessionKey = `collabbay_freemium_popup_shown_${userData.Username}`;

  if (sessionStorage.getItem(sessionKey) === "Y") return;

  const loginKey = `collabbay_login_count_${userData.Username}`;

  const currentCount =
    Number(localStorage.getItem(loginKey) || 0) + 1;

  localStorage.setItem(loginKey, String(currentCount));

const shouldShow = true;

  if (shouldShow) {
    sessionStorage.setItem(sessionKey, "Y");

    setShowFreemiumPopup(true);

    setTimeout(() => {
      setFreemiumCloseReady(true);
    }, 3000);
  }
}, [userData?.Username, isAuthenticated]);


useEffect(() => {
  if (!showFreemiumPopup) return;

  const id = setInterval(() => {
    setFreemiumProofIndex((prev) =>
      (prev + 1) % freemiumProofs.length
    );
  }, 2500);

  return () => clearInterval(id);
}, [showFreemiumPopup]);
  // ------------------------------
  // Fetch: Recently online
  // ------------------------------
  useEffect(() => {
    fetch("https://collabbayadmin.com/APIControllers/RecentlyOnlineCreators.php")
      .then((r) => r.json())
      .then((d) => {
        if (d?.status === "success") {
          setRecentOnline(d.users || []);
          setRecentCount(d.count || 0);
        }
      })
      .catch(() => {});
  }, []);




  const getStableViewCount = (listingId, liveCount) => {
  const n = Number(liveCount || 0);

  // ✅ Do not show low view counts
  if (n <= 10) return 0;

  // ✅ Only refresh the visible number every 2 days per listing
  const key = `collabbay_visible_views_${listingId}`;
  const now = Date.now();
  const twoDays = 2 * 24 * 60 * 60 * 1000;

  try {
    const saved = JSON.parse(localStorage.getItem(key) || "null");

    // First time: save and show current count
    if (!saved) {
      localStorage.setItem(
        key,
        JSON.stringify({
          count: n,
          updatedAt: now,
        })
      );
      return n;
    }

    const savedCount = Number(saved.count || 0);
    const updatedAt = Number(saved.updatedAt || 0);

    // Only update visible count every 2 days, and only if it increased
    if (now - updatedAt >= twoDays && n > savedCount) {
      localStorage.setItem(
        key,
        JSON.stringify({
          count: n,
          updatedAt: now,
        })
      );
      return n;
    }

    // Keep showing the older visible number
    return savedCount > 10 ? savedCount : 0;
  } catch (e) {
    return n;
  }
};


const fetchViewCount = (listingId) => {
  return fetch(
    "https://collabbayadmin.com/APIControllers/FetchListingViewCount.php?ListingID=" +
      encodeURIComponent(listingId),
    { credentials: "include" }
  )
    .then((r) => r.json())
    .then((d) => {
      const liveCount = d?.ok ? Number(d.ListingViews || 0) : 0;
      return getStableViewCount(listingId, liveCount);
    })
    .catch(() => 0);
};


  // ------------------------------
  // Fetch: Suggested users (brand only)
  // ------------------------------
  useEffect(() => {
    if (!userData?.Username) return;
    setLoadingSuggested(true);
    fetch(
      "https://collabbayadmin.com/APIControllers/GetSuggestedUsersForBrand.php",
      { credentials: "include" }
    )
      .then((r) => r.json())
      .then((d) => setSuggestedUsers(d?.status === "success" ? d.users : []))
      .catch(() => setSuggestedUsers([]))
      .finally(() => setLoadingSuggested(false));
  }, [userData]);

  // ------------------------------
  // Fetch: Brand listings
  // ------------------------------
  const loadBrandListings = () => {
    fetch(
      "https://collabbayadmin.com/APIControllers/RetrieveBrandListings.php",
      { credentials: "include" }
    )
      .then((r) => r.json())
      .then((json) => {
        const filtered = Array.isArray(json)
          ? json.filter((x) => x.BrandName === userData?.Username)
          : [];
        setBrandListings(filtered);
      })
      .catch(() => setBrandListings([]));
  };
  useEffect(() => {
    if (!userData?.Username) return;
    loadBrandListings();
  }, [userData]);


useEffect(() => {
  if (!Array.isArray(brandListings) || brandListings.length === 0) return;

  let cancelled = false;
  const ids = brandListings.map((x) => x?.ListingID).filter(Boolean);

  Promise.all(ids.map((id) => fetchViewCount(id).then((v) => [id, v])))
    .then((pairs) => {
      if (cancelled) return;
      const next = {};
      pairs.forEach(([id, v]) => (next[id] = v));
      setListingViewsById(next);
    })
    .catch(() => {});

  return () => {
    cancelled = true;
  };
}, [brandListings]);


  // ------------------------------
  // Fetch: Collab count
  // ------------------------------
  const fetchTotalCollabCount = () => {
    fetch("https://collabbayadmin.com/APIControllers/GetCollabCount.php", {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => {
        if (d?.status === "success") setTotalCollabCount(d.count || 0);
      })
      .catch(() => {});
  };
  useEffect(() => {
    if (!userData?.Username) return;
    fetchTotalCollabCount();
  }, [userData]);

  // ------------------------------
  // Fetch: Collabs (calendar)
  // ------------------------------
  const loadCollabs = () => {
    setLoadingCollabs(true);
    fetch("https://collabbayadmin.com/APIControllers/GetCollabCalendar.php", {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        const filtered = (Array.isArray(data) ? data : []).filter(
          (c) => c.BrandNotInterested !== "Y"
        );
        setCollabs(filtered);
      })
      .catch(() => setCollabs([]))
      .finally(() => {
        setLoadingCollabs(false);
        setBoot(false);
      });
  };
  useEffect(() => {
    loadCollabs();
  }, []);

  // ------------------------------
  // Fetch: Missed bookings
  // ------------------------------
  useEffect(() => {
    fetch("https://collabbayadmin.com/APIControllers/GetMissedBookings.php", {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d)) {
          setMissedCollabs(d);
        } else if (Array.isArray(d?.collabs)) {
          setMissedCollabs(d.collabs);
        } else {
          setMissedCollabs([]);
        }
      })
      .catch(() => {
        setMissedCollabs([]);
      });
  }, []);

  // ------------------------------
  // Fetch: “in-venue now” window
  // ------------------------------
  const fetchCurrentCollabData = () => {
    fetch(
      "https://collabbayadmin.com/APIControllers/GetCollabCalendarCurrent.php"
    )
      .then((r) => r.json())
      .then((data) => {
        const now = new Date();
        const oneHourBefore = new Date(now.getTime() - 60 * 60 * 1000);
        const fiveHoursAfter = new Date(now.getTime() + 5 * 60 * 60 * 1000);
        const filtered = (Array.isArray(data) ? data : []).filter((c) => {
          const t = new Date(c?.CreditAvailableStart);
          return c?.Status === "Accepted" && t >= oneHourBefore && t <= fiveHoursAfter;
        });
        setAlertCollabs(filtered);
        if (filtered.length > 0) {
          setCurrentCollab(filtered[0]);
        }
      })
      .catch(() => {});
  };
  useEffect(() => {
    fetchCurrentCollabData();
  }, []);

  // ------------------------------
  // Fetch: RAG (content due)
  // ------------------------------
  const loadRAG = () => {
    setLoadingRAG(true);
    fetch(
      "https://collabbayadmin.com/APIControllers/BrandGetContentRAG.php",
      { credentials: "include" }
    )
      .then((r) => r.json())
      .then((d) => {
        if (d?.success) {
          const filtered = (d.results || []).filter(
            (x) => x.Status === "YELLOW" || x.Status === "RED"
          );
          setRagItems(filtered);
        } else setRagItems([]);
      })
      .catch(() => setRagItems([]))
      .finally(() => setLoadingRAG(false));
  };
  useEffect(() => {
    if (!userData?.Username) return;
    loadRAG();
  }, [userData]);

  // ------------------------------
  // Helpers
  // ------------------------------
  const getOrdinalSuffix = (d) => {
    if (d > 3 && d < 21) return "th";
    switch (d % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return "";
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const hours = date.getHours() % 12 || 12;
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "PM" : "AM";
    const today = new Date();
    const diffDays = Math.floor(
      (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) -
        Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) /
        (1000 * 60 * 60 * 24)
    );
    const when =
      diffDays === 0
        ? "today"
        : diffDays > 0
        ? `in ${diffDays} day${diffDays > 1 ? "s" : ""}`
        : `${Math.abs(diffDays)} day${
            Math.abs(diffDays) > 1 ? "s" : ""
          } ago`;
    return `${day}${getOrdinalSuffix(day)} ${month} ${hours}:${minutes}${ampm} (${when})`;
  };

  const formatDateParts = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return { main: "", when: "" };

    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const hours = date.getHours() % 12 || 12;
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "PM" : "AM";
    const main = `${day}${getOrdinalSuffix(day)} ${month} ${hours}:${minutes}${ampm}`;

    const today = new Date();
    const diffDays = Math.floor(
      (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) -
        Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) /
        (1000 * 60 * 60 * 24)
    );

    const when =
      diffDays === 0
        ? "today"
        : diffDays > 0
        ? `in ${diffDays} day${diffDays > 1 ? "s" : ""}`
        : `${Math.abs(diffDays)} day${
            Math.abs(diffDays) > 1 ? "s" : ""
          } ago`;

    return { main, when };
  };

  function EmphasizedWhen({ text }) {
    const parts = String(text).split(/(\d+)/);
    return (
      <>
        {parts.map((p, i) =>
          /^\d+$/.test(p) ? (
            <strong key={i} className="whenBig">
              {p}
            </strong>
          ) : (
            p
          )
        )}
      </>
    );
  }

  const getStatus = (c) => {
    if (c?.Status === "Invited") return "Invited";
    if (c?.Status === "Cancelled") return "Cancelled";
    const now = new Date();
    const credit = new Date(c?.CreditAvailableStart);
    if (credit < now) return c?.Redeemed ? "Redeemed" : "Expired";
    if (c?.Status === "Accepted") return "Upcoming";
    if (c?.Status === "Pending") return "Pending";
    return "Pending";
  };

  const statusClass = (s) => {
    const m = {
      Expired: "expired",
      Redeemed: "redeemed",
      Upcoming: "upcoming",
      Pending: "pending",
      Cancelled: "cancelled",
      Invited: "invited",
    };
    return m[s] || "pending";
  };

  const navigateTo = (screen, params) => {
const subscriptionGated = [
  "BrowseInviteCreators",
  "MyCalendar",
  "MyActivityFeed",
  "ContentBank",
  "BrandAIAnalysis", // ✅ add
];


    if (!userData?.Username && screen !== "LogInPage" && screen !== "SignUpPage") {
      return navigation.navigate("LogInPage");
    }
    if (!isAuthenticated && subscriptionGated.includes(screen)) {
      return openSubscribe();
    }
    navigation.navigate(screen, params);
  };

  const openSubscribe = () => {
    // ✅ Always hard-reload the real subscribe page on collabbayadmin.com
    if (typeof window !== "undefined" && window.location) {
      window.location.href = "https://collabbayadmin.com/subscribe";
      return;
    }

    // Fallback (in case this ever runs in a purely native environment)
    if (navigation?.navigate) {
      navigation.navigate("PaymentPage");
    }
  };

const openPreviewBooking = (c) => {
  navigation.navigate("PreviewBooking", {
    CollabID: c?.CollabID,
    Promoter: c?.Promoter,     // ✅ this is the creator username
    Username: c?.Promoter,     // ✅ optional (your PreviewBooking supports this too)
  });
};

  const handleRowClick = (c) => {
    const freeRow =
      !isAuthenticated &&
      trialFreeCollabID &&
      c?.CollabID === trialFreeCollabID;

    // not logged in -> go login
    if (!userData?.Username) {
      return navigation.navigate("LogInPage");
    }

    // logged in but not subscribed -> blurred rows go to PreviewBooking
    if (!isAuthenticated && !freeRow) {
      return openPreviewBooking(c);
    }

    // subscribed (or freeRow) -> real booking request
    navigation.navigate("BookingRequest", { CollabID: c?.CollabID });
  };

  const handleEditBanner = (listingID) => {
    const url = `https://collabbayadmin.com/ListingImageUpload.html?ListingID=${encodeURIComponent(
      listingID
    )}`;
    window.open(url, "_blank", "noopener");
  };

  const handleHelpClick = () => {
    window.location.href =
      "mailto:info@collab-bay.com?subject=Help%20with%20Collabbay&body=Hi%20Collabbay%20team,%0D%0A%0D%0A";
  };

  const handleFaqClick = () => {
    window.location.href = "https://collabbayadmin.com/FAQs";
  };

  const handleMapClick = () => {
    window.location.href = "https://collabbayadmin.com/venues";
  };



const submitBrandSegment = () => {
  if (!segmentQuality || !segmentRhythm || segmentSaving) return;

  setSegmentSaving(true);
  setSegmentError("");

  fetch("https://collabbayadmin.com/APIControllers/UpdateBrandSegment.php", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      qualityInput: segmentQuality,
      rhythmInput: segmentRhythm,
    }),
  })
    .then((r) => r.json())
    .then((d) => {
      if (d?.success) {
        setUserData((prev) => ({
          ...prev,
          BrandSegment: d.BrandSegment,
        }));
        setShowSegmentModal(false);
        setSegmentStep(1);
        setSegmentQuality("");
        setSegmentRhythm("");
      } else {
        setSegmentError(d?.message || "Could not save your preferences.");
      }
    })
    .catch(() => {
      setSegmentError("Could not save your preferences.");
    })
    .finally(() => {
      setSegmentSaving(false);
    });
};

  // ------------------------------
  // Render
  // ------------------------------
  return (
    <div className="wrap">

{showFreemiumPopup && (
  <div className="modalOverlay">
    <div className="freemiumModal">

      <button
        className={`freemiumClose ${!freemiumCloseReady ? "locked" : ""}`}
        onClick={() => {
          if (!freemiumCloseReady) return;
          setShowFreemiumPopup(false);
        }}
      >
        ×
      </button>

<div className="freemiumTitle">
  Want to fill {userData?.BrandName || "your venue"} with{" "}
  <strong>customers</strong>, our premium creators and none of the faff?
</div>


<button
  className="freemiumTryBtn"
  onClick={openSubscribe}
>
  <span
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      color: "#166534",
      fontWeight: 900,
    }}
  >
    <span
      style={{
        width: 18,
        height: 18,
        borderRadius: 999,
        background: "#22c55e",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 900,
      }}
    >
      ✓
    </span>

    Try boosted footfall with full access
  </span>
</button>

      <div className="freemiumProof">
        <span className="verifyTick">✓</span>

        <span>
          {freemiumProofs[freemiumProofIndex]}
        </span>
      </div>

    </div>
  </div>
)}

{showSegmentModal && (
  <div className="modalOverlay" role="dialog" aria-modal="true">
    <div
      className="modal"
      style={{
        maxWidth: 620,
        borderRadius: 28,
        padding: 28,
        border: "1px solid rgba(17,24,39,0.08)",
        boxShadow: "0 30px 90px rgba(0,0,0,0.32)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              color: "#6b7280",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Customisation
          </div>

          <div style={{ fontSize: 30, fontWeight: 950, color: "#111827", lineHeight: 1.08 }}>
            <span
              style={{
                background: "linear-gradient(90deg, #111827 0%, #d6c900 45%, #FDF05D 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Tailor
            </span>{" "}
            your collabs
          </div>

          <div style={{ display: "flex", gap: 7, marginTop: 18 }}>
            {[1, 2, 3].map((n) => (
              <span
                key={n}
                style={{
                  width: segmentStep === n ? 24 : 8,
                  height: 8,
                  borderRadius: 999,
                  background: segmentStep === n ? "#111827" : "#e5e7eb",
                  transition: "all 0.2s ease",
                }}
              />
            ))}
          </div>
        </div>

        {segmentStep === 1 && (
          <>
            <div style={{ fontSize: 17, fontWeight: 900, color: "#111827" }}>
              How would someone describe your venue?
            </div>

            <div style={{ display: "grid", gap: 10 }}>
{[
  { label: "Michelin-starred", badge: "★ £££" },
  { label: "Refined Elegance", badge: "£££" },
  { label: "Local Gem", badge: "££" },
  { label: "Casual and Quick", badge: "£" },
].map((item) => {
  const option = item.label;
  const selected = segmentQuality === option;

  return (
    <button
      key={option}
      type="button"
      onClick={() => setSegmentQuality(option)}
      style={{
        width: "100%",
        padding: "15px 16px",
        borderRadius: 16,
        border: selected ? "2px solid #111827" : "1px solid #e5e7eb",
        background: selected ? "rgba(253,240,93,0.35)" : "#ffffff",
        color: "#111827",
        fontWeight: 850,
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        boxShadow: selected ? "0 10px 26px rgba(17,24,39,0.08)" : "none",
      }}
    >
      <span>{option}</span>

      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            background: "#f3f3f3",
            border: "1px solid #e5e7eb",
            borderRadius: 999,
            padding: "5px 10px",
            fontSize: 11,
            fontWeight: 900,
            color: "#111827",
          }}
        >
          {item.badge}
        </span>

        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: 999,
            border: selected ? "6px solid #111827" : "2px solid #d1d5db",
            background: "#fff",
          }}
        />
      </span>
    </button>
  );
})}
            </div>

            <button
              type="button"
              className="ctaBtn"
              disabled={!segmentQuality}
              onClick={() => setSegmentStep(2)}
              style={{
                opacity: segmentQuality ? 1 : 0.45,
                padding: "13px 16px",
                borderRadius: 999,
                marginTop: 4,
              }}
            >
              Next
            </button>
          </>
        )}

        {segmentStep === 2 && (
          <>
            <div style={{ fontSize: 17, fontWeight: 900, color: "#111827" }}>
              What rhythm of influencer collab-requests suit your business?
            </div>

            <div style={{ display: "grid", gap: 10 }}>
            {[
  { label: "Any interest that we can get", badge: "▲▲▲" },
  { label: "A steady flow to choose from", badge: "▲▲" },
  { label: "Occasional, select collabs", badge: "▲" },
].map((item) => {
  const option = item.label;
  const selected = segmentRhythm === option;

  return (
    <button
      key={option}
      type="button"
      onClick={() => setSegmentRhythm(option)}
      style={{
        width: "100%",
        padding: "15px 16px",
        borderRadius: 16,
        border: selected ? "2px solid #111827" : "1px solid #e5e7eb",
        background: selected ? "rgba(253,240,93,0.35)" : "#ffffff",
        color: "#111827",
        fontWeight: 850,
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        boxShadow: selected ? "0 10px 26px rgba(17,24,39,0.08)" : "none",
      }}
    >
      <span>{option}</span>

      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            background: "#f3f3f3",
            border: "1px solid #e5e7eb",
            borderRadius: 999,
            padding: "5px 10px",
            fontSize: 11,
            fontWeight: 900,
            color: "#111827",
            letterSpacing: "0.08em",
          }}
        >
          {item.badge}
        </span>

        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: 999,
            border: selected ? "6px solid #111827" : "2px solid #d1d5db",
            background: "#fff",
          }}
        />
      </span>
    </button>
  );
})}
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <button type="button" className="ghostBtn" onClick={() => setSegmentStep(1)} style={{ borderRadius: 999 }}>
                Back
              </button>

              <button
                type="button"
                className="ctaBtn"
                disabled={!segmentRhythm}
                onClick={() => setSegmentStep(3)}
                style={{
                  flex: 1,
                  opacity: segmentRhythm ? 1 : 0.45,
                  borderRadius: 999,
                }}
              >
                Next
              </button>
            </div>
          </>
        )}

        {segmentStep === 3 && (
          <>
            <div style={{ fontSize: 17, fontWeight: 900, color: "#111827" }}>
              Confirm your preferences
            </div>

            <div
              style={{
                background: "linear-gradient(180deg,#ffffff,#f9fafb)",
                border: "1px solid #e5e7eb",
                borderRadius: 18,
                padding: 16,
                display: "grid",
                gap: 10,
              }}
            >
              <div><strong>Venue style:</strong> {segmentQuality}</div>
              <div><strong>Collab rhythm:</strong> {segmentRhythm}</div>
            </div>

            {segmentError ? (
              <div style={{ color: "#eb5757", fontWeight: 800 }}>{segmentError}</div>
            ) : null}

            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <button
                type="button"
                className="ghostBtn"
                onClick={() => setSegmentStep(2)}
                disabled={segmentSaving}
                style={{ borderRadius: 999 }}
              >
                Back
              </button>

              <button
                type="button"
                className="ctaBtn"
                onClick={submitBrandSegment}
                disabled={segmentSaving}
                style={{
                  flex: 1,
                  borderRadius: 999,
                  padding: "13px 16px",
                }}
              >
                {segmentSaving ? "Saving…" : "Confirm"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  </div>
)}

      {boot && (
        <div className="bootOverlay">
          <div>
            <div className="spinner" />
            <div className="bootMsg">{bootMsg}</div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <aside className="sidebar">
        <div className="logoRow">
          <span className="logo">
            <img
              alt="Collabbay"
              src="https://collabbayadmin.com/CollabbayLogoDark.png"
            />
          </span>
          <div>
            <div className="appName">
              collab<b>bay.</b>
            </div>
            <div className="modeTag">DESKTOP</div>
          </div>
        </div>

        <div className="nav">
          <button onClick={() => navigation.navigate("MyPanel")}>
            <DotIcon /> Home
          </button>

          <button
            onClick={() => {
              if (userData?.Username) navigateTo("MyOffers");
              else navigation.navigate("LogInPage");
            }}
          >
            <LaptopIcon /> My Offers
          </button>

          <button onClick={() => navigateTo("BrowseCreators")}>
            <SearchIcon /> Browse Creators{" "}
            {userData?.Username && !isAuthenticated ? (
              <span className="pill">Locked</span>
            ) : null}
          </button>

          <button onClick={() => navigateTo("MyCalendar")}>
            <CalendarIcon /> Calendar{" "}
            {userData?.Username && !isAuthenticated ? (
              <span className="pill">Locked</span>
            ) : null}
          </button>

          <button onClick={() => navigateTo("MyActivityFeed")}>
            <ChatIcon /> Activity{" "}
            {userData?.Username && !isAuthenticated ? (
              <span className="pill">Locked</span>
            ) : null}
          </button>


<button onClick={() => navigateTo("ContentBank")}><ContentIcon /> My Content Bank <span className="newBadge">New <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 4, display: "inline-block", verticalAlign: "middle" }} aria-hidden><path d="M9 18l6-6-6-6" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></span>{userData?.Username && !isAuthenticated ? (<span className="pill">Locked</span>) : null}</button>


<button onClick={() => navigateTo("BrandAIAnalysis")}>
  <BoltIcon /> Brand AI Analysis{" "}
  <span className="newBadge">
    New{" "}
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      style={{
        marginLeft: 4,
        display: "inline-block",
        verticalAlign: "middle",
      }}
      aria-hidden
    >
      <path
        d="M9 18l6-6-6-6"
        stroke="#000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
  {userData?.Username && !isAuthenticated ? (
    <span className="pill">Locked</span>
  ) : null}
</button>


          <button
            onClick={() => {
              if (userData?.Username) navigateTo("BusinessAccountPage");
              else window.location.href = "https://collabbayadmin.com/SignIn";
            }}
          >
            <UserIcon /> Account
          </button>

          <button className="cta" onClick={() => navigateTo("GuidedOfferSetup")}>
            + Create offer
          </button>
        </div>

        {/* Upgrade CTA in sidebar */}
        {userData?.Username && !isAuthenticated && (
          <button className="upgradeBtn" onClick={openSubscribe}>
            <span className="upgradeBtnTitleOnly">Unlock full access</span>
          </button>
        )}

        {/* Q&A: CTA / FAQ / MAP */}
        <div className="sideActions">
          {/* What is this? -> FAQ */}
          <button className="sideActionCard" type="button" onClick={handleFaqClick}>
            <div className="sideActionTitle">New? FAQ Page</div> <span className="newBadge">New</span>
            <div className="sideActionSubtitle">Read the Collabbay FAQ</div>
          </button>

          {/* Who else is using it? -> MAP + DATA */}
          <button
            type="button"
            className="sideActionCard"
            onClick={handleMapClick}
          >
            <div className="mapActionRow">
              <MapIcon />
              <div>
                <div className="sideActionTitle">
                  See your peers <span className="newBadge">New</span>
                </div>
                <div className="sideActionSubtitle">
                  View venues using Collabbay
                </div>
              </div>
            </div>
          </button>
        </div>

        {/* Sidebar: Suggested creators */}
        {userData?.Username && (
          <div className="sideCreators">
            <div className="sideCreatorsHeader">
              <div className="sideCreatorsTitle">Suggested creators</div>
              <div className="sideCreatorsHint">Tap to invite</div>
            </div>

            {loadingSuggested ? (
              <div className="muted" style={{ fontSize: 12 }}>
                Loading…
              </div>
            ) : suggestedUsers?.length > 0 ? (
              <div className="sideList">
                {suggestedUsers.map((u, i) => {
                  const subject = `Collab invite from ${userData.Username}`;
                  const body = `Hi ${u.Username},\n\nWe'd love to collaborate with you on Collabbay! Check out our offer and let us know if you're interested.\n\nYou can visit Collabbay to check it out and book to visit us.\n\nThanks,\n${userData.Username}`;
                  const mailto = `mailto:${u.UserEmail}?subject=${encodeURIComponent(
                    subject
                  )}&body=${encodeURIComponent(body)}`;
                  return (
                    <button
                      key={i}
                      className="sideCreatorBtn"
                      onClick={() => (window.location.href = mailto)}
                      title={`Invite ${u.Username}`}
                    >
                      <span className="sideAvatar">
                        <img alt="" src={u.ProfileImage} />
                      </span>
                      <div className="sideMeta">
                        <div className="sideName">{u.Username}</div>
                        <div className="sideSub">
                          {u.PlatformFollowers} followers
                        </div>
                      </div>
                      <ChevronRightIcon size={16} />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="muted" style={{ fontSize: 12 }}>
                No suggestions yet
              </div>
            )}
          </div>
        )}

        {/* Where can I get help? -> Help button at very bottom */}
        <button type="button" className="sideHelpBtn" onClick={handleHelpClick}>
          <span className="sideHelpMain">We are here to help 24/7</span>
          <span className="sideHelpSub">Get in touch</span>
        </button>
      </aside>

      {/* Main */}
      <main className="main">
        {/* Top banner */}
        <div className="top">
<div className="hello">


<button
  type="button"
  className={`hiAvatarBtn ${
    userData?.ProfileImage && String(userData.ProfileImage).trim()
      ? ""
      : "hiAvatarBtn--empty"
  }`}
  onClick={() =>
    (window.location.href =
      "https://collabbayadmin.com/UpdateProfile.html")
  }
  title="Edit your logo"
  aria-label="Edit your logo"
>
  {userData?.ProfileImage && String(userData.ProfileImage).trim() ? (
    <img alt="" src={userData.ProfileImage} />
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 20h9"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  )}
</button>


  <div>

              <div className="hi">
                Hi{userData?.Username ? `, ${userData.Username}` : ""} 👋
              </div>
              <div className="badge">
                <BoltIcon />
                Grow with creator visits
              </div>
            </div>
          </div>

          {recentOnline?.length > 0 && (
            <div className="recent">
              <span>Recently online</span>
              <div className="avatars">
                {recentOnline.slice(0, 3).map((u, idx) => (
                  <div className="av" key={idx} style={{ zIndex: 3 - idx }}>
                    <img alt="" src={u?.ProfileImage} />
                    {idx === 0 && <span className="greenDot" />}
                  </div>
                ))}
                {recentCount > 3 && (
                  <span className="plus">+{recentCount - 3}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Upgrade strip in main (if logged in but not subscribed) */}
        {userData?.Username && !isAuthenticated && (
          <div className="card upgradeStrip">
            <div className="upgradeStripText">
              <div className="upgradeStripTitle">Unlock full access</div>
              <div className="upgradeStripSub">
                View your calendar, creator profiles and activity feed without
                limits.
              </div>
            </div>
            <button className="upgradeStripBtn" onClick={openSubscribe}>
              Upgrade now
              <ChevronRightIcon size={16} />
            </button>
          </div>
        )}

        {/* Grid */}
        <div className="grid">
          {/* Left column */}
          <div className="col">
            {/* Recap banner (video + venue code + CTA) */}
            <div className="recap">
              <div className="recapTile recapVideo" aria-label="Intro video">
                <video autoPlay muted playsInline loop>
                  <source
                    src="https://collabbayadmin.com/Admin%20Web%20Intro%20%20Collabbay%20App%20(1).mp4"
                    type="video/mp4"
                  />
                </video>
              </div>

              <div className="recapTile recapCard">
                <div className="recapText">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <div className="recapLabel">Your venue code is</div>
                    <button
                      type="button"
                      className="infoBtn"
                      onClick={() => setShowVenueCodeInfo(true)}
                      aria-label="What is this code?"
                    >
                      i
                    </button>
                  </div>
                  <div className="recapValue">{venueCode || "--"}</div>
                </div>

                <button
                  className="recapCTA"
                  onClick={() => navigateTo("MyOffers")}
                  title="Go to all collabs"
                  style={{
                    background: "#fdf05d",
                  }}
                >
                  Go to all collabs
                  <span className="countPill">
                    {typeof totalCollabCount === "number"
                      ? totalCollabCount
                      : 0}
                  </span>
                  <ChevronRightIcon size={18} />
                </button>
              </div>
            </div>

            {/* Collab calendar */}
            <div className="card">
              <div className="cardHeader">
                <div className="cardTitle">My Collabs</div>
              </div>

              <div className="controlsBar">
                <div className="searchBox" title="Search by creator name">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="#bbb"
                    aria-hidden
                  >
                    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 5 1.5-1.5-5-5zM9.5 14A4.5 4.5 0 119.5 5a4.5 4.5 0 010 9z" />
                  </svg>
                  <input
                    placeholder="Search creator…"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                  />
                  {(searchName || statusFilter !== "All") && (
                    <button
                      className="clearBtn"
                      onClick={() => {
                        setSearchName("");
                        setStatusFilter("All");
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>

                <select
                  className="sortSelect"
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                  title="Sort"
                >
                  <option value="Soonest">Sort: Soonest</option>
                  <option value="Latest">Sort: Latest</option>
                  <option value="Created-Newest">Sort: Created (Newest)</option>
                  <option value="Created-Oldest">Sort: Created (Oldest)</option>
                </select>
              </div>

              {loadingCollabs ? (
                <div className="muted">Loading…</div>
              ) : displayCollabs.length === 0 ? (
                <div className="muted">No requests yet.</div>
              ) : (
                <div className="list">
                  {filteredCollabs.map((c, idx) => {
             const s = getStatus(c);
const isProposal = String(c?.IsProposal || "").toUpperCase() === "Y";

const isFreeRow =
  !isAuthenticated &&
  trialFreeCollabID &&
  c?.CollabID === trialFreeCollabID;

const shouldBlur = !isAuthenticated && !isFreeRow;
const promoterName = c?.Promoter || "";
                    return (
                      <div
                        key={c?.CollabID || idx}
                        className={`row ${isFreeRow ? "free" : ""} ${isProposal ? "proposal" : ""}`}
                        onClick={() => handleRowClick(c)}
                        title="Open collab"
                      >
                        {isFreeRow && (
                          <div
                            style={{
                              position: "absolute",
                              top: -8,
                              left: 14,
                              background: "var(--ok)",
                              color: "#000",
                              fontWeight: 800,
                              borderRadius: 999,
                              padding: "4px 8px",
                              fontSize: 12,
                            }}
                          >
                            Unlocked for you
                          </div>
                        )}

                        {isProposal && (
  <div className="proposalBadge">
  ✦ Proposal
  </div>
)}


{isProposal && (
  <div className="proposalInfo">
    Curated for you
  </div>
)}

                        <div className="rowTop">
                          {(() => {
                            const { main, when } = formatDateParts(
                              c?.CreditAvailableStart
                            );
                            return (
                              <div className="dateWrap">
                                <span className="dateTxt">{main}</span>
                                {when && (
                                  <span className="whenPill">
                                    <EmphasizedWhen text={when} />
                                  </span>
                                )}

                                {c?.PlatformFollowers && (
                                  <div
                                    style={{
                                      display: "inline-block",
                                      marginTop: 4,
                                      backgroundColor: "#f3f4f6",
                                      borderRadius: 8,
                                      padding: "4px 8px",
                                      fontSize: 12,
                                      color: "#4b5563",
                                      border:
                                        "1px solid rgba(148,163,184,0.35)",
                                    }}
                                  >
                                    {getFollowerRange(
                                      parseFollowers(c.PlatformFollowers)
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          <div className={`status ${statusClass(s)}`}>
                            <DotIcon />
                            {s}
                          </div>
                        </div>

                        <div className="nameRow">
                          <div
                            className={`avatar ${
                              shouldBlur ? "blurredAvatar" : ""
                            }`}
                          >
                            <img
                              alt=""
                              src={
                                c?.PromoterProfileImage &&
                                c.PromoterProfileImage.trim() !== ""
                                  ? c.PromoterProfileImage
                                  : "https://collabbayadmin.com/defaults/avatar-placeholder.png"
                              }
                            />
                          </div>
                          <div
                            className={`promoter ${
                              shouldBlur ? "blurredName" : ""
                            }`}
                          >
                            {promoterName}
                          </div>
                        </div>

                        <div className="chipRow">
                          {c?.NonCreditOffer && (
                            <div className="chip offerChip">
                              <span className="label"></span>
                              {c.NonCreditOffer.length > 60
                                ? c.NonCreditOffer.slice(0, 60) + "…"
                                : c.NonCreditOffer}
                            </div>
                          )}

                          {c?.OnBehalfOf?.trim() && (
                            <div className="chip clientChip">
                              <span className="label">Client</span> |{" "}
                              {c.OnBehalfOf}
                            </div>
                          )}
                        </div>

                        {/* Big chevron on the right */}
                        <div className="rowChevron">
                          <ChevronRightIcon size={22} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right column */}
          <div className="col">
            {/* No offers CTA */}
            {userData?.Username && brandListings.length === 0 && (
              <div className="card">
                <div
                  style={{
                    background: "var(--brand)",
                    color: "#000",
                    borderRadius: 14,
                    padding: 16,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 18,
                      marginBottom: 4,
                    }}
                  >
                    Post an offer to start taking bookings
                  </div>
                  <div className="muted" style={{ color: "#000", opacity: 0.65 }}>
                    You're one step away.
                  </div>
                  <div style={{ height: 10 }} />
                  <button
                    className="ctaBtn"
                    onClick={() => navigateTo("GuidedOfferSetup")}
                  >
                    Create an Offer
                  </button>{" "}
                  <button
                    className="ghostBtn"
                    onClick={() => navigateTo("NotificationsPage")}
                  >
                    Notifications
                  </button>
                </div>
              </div>
            )}

            {/* My Offers */}
            <div className="card">
              <div className="cardHeader">
                <div className="cardTitle">My Offers</div>
                <button className="linkBtn" onClick={() => navigateTo("MyOffers")}>
                  Go to dashboard{" "}
                  <ChevronRightIcon size={16} color="#000000" />
                </button>
              </div>

              {brandListings.length === 0 ? (
                <div className="muted">No offers yet. Click “Create offer”.</div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 12,
                  }}
                >
                  {brandListings.map((item, idx) => (
                    <div className="offer" key={idx}>
                      <div className="offerImg">
                        <img alt="" src={getCoverImage(item.LogoImage)} />
                        {Number(listingViewsById?.[item.ListingID] || 0) > 0 && (
  <div className="viewsBadge" title="Offer views">
    <EyeIcon size={14} />
    <span>{listingViewsById[item.ListingID]} views</span>
  </div>
)}

                        <button
                          className="editFab"
                          onClick={() => handleEditBanner(item.ListingID)}
                        >
                          <EditIcon />
                        </button>
                        {!item.LogoImage ||
                        item.LogoImage ===
                          "https://collabbayadmin.com/NoListingImage.jpg" ? (
                          <div
                            style={{
                              position: "absolute",
                              bottom: 8,
                              left: 8,
                              background: "#eb5757",
                              color: "#fff",
                              borderRadius: 999,
                              padding: "6px 10px",
                              fontSize: 12,
                              fontWeight: 800,
                            }}
                          >
                            Add a banner image
                          </div>
                        ) : null}
                      </div>
                      <div className="offerBody">
                        <div className="offerSub">
                          Created {prettyShortDate(item.Created_At)}
                        </div>
                        <div className="offerTitle" style={{ margin: "6px 0" }}>
                          {item.AdHeader?.length > 200
                            ? item.AdHeader.slice(0, 200) + "…"
                            : item.AdHeader}
                        </div>

                        {item.OnBehalfOf && item.OnBehalfOf.trim() !== "" && (
                          <div className="chip" style={{ marginBottom: 8 }}>
                            Client | {item.OnBehalfOf}
                          </div>
                        )}

                        <div className="offerSub" style={{ marginBottom: 10 }}>
                          {(item.ListingDescription || "").length > 120
                            ? item.ListingDescription.slice(0, 120) + "…"
                            : item.ListingDescription}
                          <br />
                          <strong>{item.Location}</strong>
                        </div>

                        {item.PauseLiveToggle === "Live" ? (
                          <span className="live">
                            <DotIcon /> Live
                          </span>
                        ) : (
                          <span className="paused">
                            <DotIcon /> Paused
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Missed Bookings Section */}
            {missedCollabs.length > 0 && (
              <div className="card">
                <div className="cardHeader">
                  <div className="cardTitle">Missed These Bookings?</div>
                </div>

                <div className="list">
                  {missedCollabs.map((m, i) => {
                    const blurred = !isAuthenticated;

                    const handleClick = () => {
                      if (!isAuthenticated) {
                        openPreviewBooking(m);
                      } else {
                        navigation.navigate("BookingRequest", {
                          CollabID: m.CollabID,
                        });
                      }
                    };

                    return (
                      <div
                        key={i}
                        className="row"
                        onClick={handleClick}
                        style={{
                          position: "relative",
                          background: "rgba(255,255,255,0.97)",
                          cursor: "pointer",
                        }}
                      >
                        <div className="nameRow">
                          <div
                            className={`avatar ${
                              blurred ? "blurredAvatar" : ""
                            }`}
                          >
                            <img
                              alt=""
                              src={
                                m.PromoterProfileImage ||
                                "https://collabbayadmin.com/defaults/avatar-placeholder.png"
                              }
                            />
                          </div>
                          <div
                            className={`promoter ${
                              blurred ? "blurredName" : ""
                            }`}
                          >
                            {m.Promoter}
                          </div>
                        </div>

                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          Requested to visit on {formatDate(m.CreditAvailableStart)}
                        </div>

                        <div
                          style={{
                            display: "inline-block",
                            marginTop: 4,
                            backgroundColor: "#f3f4f6",
                            borderRadius: 8,
                            padding: "4px 8px",
                            fontSize: 12,
                            color: "#4b5563",
                            border: "1px solid rgba(148,163,184,0.35)",
                          }}
                        >
                          {getFollowerRange(parseFollowers(m.PlatformFollowers))}
                        </div>

                        {m?.OnBehalfOf?.trim() && (
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              marginTop: 6,
                              background: "rgba(249,250,251,1)",
                              border: "1px solid #d1d5db",
                              borderRadius: 12,
                              padding: "6px 10px",
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#111827",
                              width: "fit-content",
                            }}
                          >
                            <span style={{ fontWeight: 800, opacity: 0.9 }}>
                              Client
                            </span>{" "}
                            | {m.OnBehalfOf}
                          </div>
                        )}

                        {/* Big chevron on right of missed booking row */}
                        <div className="rowChevron">
                          <ChevronRightIcon size={22} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Content due (RAG) */}
            <div className="card">
              <div className="cardHeader">
                <div className="cardTitle">Content due</div>
              </div>
              {loadingRAG ? (
                <div className="muted">Loading…</div>
              ) : ragItems.length === 0 ? (
                <div className="muted">No pending content.</div>
              ) : (
                <div className="rag">
                  {ragItems.map((x, i) => {
                    const due = new Date(x.LatestDeliveryDate);
                    const daysLeft = Math.floor(
                      (Date.UTC(due.getFullYear(), due.getMonth(), due.getDate()) -
                        Date.UTC(
                          new Date().getFullYear(),
                          new Date().getMonth(),
                          new Date().getDate()
                        )) /
                        (1000 * 60 * 60 * 24)
                    );
                    const isRed = x.Status === "RED";
                    return (
                      <div
                        key={i}
                        className={`ragItem ${isRed ? "red" : "yellow"}`}
                      >
                        <div className="ragTitle">
                          {isRed
                            ? "Content overdue."
                            : `Pending content, due in ${daysLeft} day${
                                daysLeft === 1 ? "" : "s"
                              }`}
                        </div>
                        <div className="ragSub">from {x.Promoter}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* “Influencer at your venue now?” alert */}
        {alertCollabs.length > 0 && currentCollab && (
          <div className="card" style={{ border: "1px solid var(--brand)" }}>
            <div className="cardHeader" style={{ alignItems: "center", gap: 10 }}>
              <div className="cardTitle">Influencer at your venue now?</div>
              <button className="ctaBtn" onClick={() => setShowCollabModal(true)}>
                View instructions
              </button>
            </div>
            <div className="muted">You have a scheduled collab in the current window.</div>
          </div>
        )}
      </main>

      {/* Login prompt */}
      {showLoginPrompt && !userData?.Username && (
        <Modal onClose={() => {}}>
          <h3>
            Join Collabbay for Business<span className="brand">.</span>
          </h3>
          <p className="muted">
            Access 480+ creators and 20m+ combined followers. Start posting
            offers and accept bookings.
          </p>
          <div className="hr" />
          <div className="grid2">
            <button
              className="ghostBtn"
              onClick={() =>
                (window.location.href = "https://collabbayadmin.com/SignIn")
              }
            >
              Log in
            </button>
            <button
              className="ctaBtn"
              onClick={() =>
                (window.location.href = "https://collabbayadmin.com/SignUp")
              }
            >
              Create account
            </button>
          </div>
        </Modal>
      )}

      {/* Venue code info modal */}
      {showVenueCodeInfo && (
        <Modal onClose={() => setShowVenueCodeInfo(false)}>
          <h3>Using your venue code</h3>
          <p className="muted">
            Give this code to influencers when they visit so that they can
            log their visit on their app.
          </p>
        </Modal>
      )}

      {/* Collab now modal */}
      {showCollabModal && currentCollab && (
        <Modal onClose={() => setShowCollabModal(false)}>
          <div className="cardHeader" style={{ marginBottom: 6 }}>
            <h3 style={{ margin: 0 }}>Are you collabing now?</h3>
          </div>
          <p className="muted" style={{ marginTop: 0 }}>
            You have an upcoming collab with{" "}
            <strong>{currentCollab?.BrandName || "brand"}</strong>.
          </p>
          <div className="hr" />
          <ol style={{ lineHeight: 1.8, margin: 0, paddingLeft: 18 }}>
            <li>Welcome the creator. 👋</li>
            <li>Provide the agreed offer or service. 🎉</li>
            <li>The creator will prepare their content on-site. 🔗</li>
            <li>Scan the creator’s QR code before they leave to confirm. 🗺</li>
            <li>Await the content upload as per the agreed timeline. 📤</li>
          </ol>
          <div className="hr" />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button className="ghostBtn" onClick={() => setShowCollabModal(false)}>
              Close
            </button>
            <button
              className="ctaBtn"
              onClick={() =>
                navigation.navigate("BookingRequest", {
                  CollabID: currentCollab.CollabID,
                })
              }
            >
              Go to Collab
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------------- Icons ---------------- */
function DotIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff" aria-hidden>
      <circle cx="12" cy="12" r="6" />
    </svg>
  );
}
function BoltIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="#FFD400" aria-hidden>
      <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
    </svg>
  );
}
function LaptopIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" aria-hidden>
      <path d="M4 5h16v10H4V5zm-2 12h20v2H2v-2z" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" aria-hidden>
      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 5 1.5-1.5-5-5zM9.5 14A4.5 4.5 0 119.5 5a4.5 4.5 0 010 9z" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" aria-hidden>
      <path d="M7 2h2v2h6V2h2v2h3v18H4V4h3V2zm13 8H6v10h14V10z" />
    </svg>
  );
}
function ChatIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" aria-hidden>
      <path d="M2 3h20v14H6l-4 4V3z" />
    </svg>
  );
}
function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" aria-hidden>
      <path d="M12 2a5 5 0 110 10 5 5 0 010-10zm0 12c4.418 0 8 2.239 8 5v3H4v-3c0-2.761 3.582-5 8-5z" />
    </svg>
  );
}

function ContentIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" aria-hidden>
      <path d="M4 4h16v2H4V4zm0 5h16v2H4V9zm0 5h10v2H4v-2zm0 5h10v2H4v-2z" />
    </svg>
  );
}



function ChevronRightIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <polyline
        points="9 6 15 12 9 18"
        stroke="black"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" aria-hidden>
      <path d="M3 17.25V21h3.75l11-11L14 6.25l-11 11zM21.41 6.34l-3.75-3.75-2.12 2.12 3.75 3.75 2.12-2.12z" />
    </svg>
  );
}
function MapIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#FDF05D" aria-hidden>
      <path d="M9 3l6 2.25L21 3v15l-6 2.25L9 18 3 20V5l6-2zM9 5.14v11.72l6 2.14V7.28L9 5.14z" />
    </svg>
  );
}

function EyeIcon({ size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
        stroke="#fff"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="2" />
    </svg>
  );
}


/* ---------------- Small helpers ---------------- */
function prettyShortDate(dateString) {
  const d = new Date(dateString);
  if (isNaN(d)) return "";
  const day = d.getDate();
  const month = d.toLocaleString("default", { month: "short" });
  const year = d.getFullYear();
  const nowYear = new Date().getFullYear();
  return `${day} ${month}${year !== nowYear ? `, ${year}` : ""}`;
}

/* ---------------- Modal ---------------- */
function Modal({ children, onClose }) {
  return (
    <div className="modalOverlay" role="dialog" aria-modal="true">
      <div className="modal">
        <div className="modalHeader">
          <span />
          <button className="x" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div>{children}</div>
      </div>
    </div>
  );
}
